import { ReactNode, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import { Sidebar } from 'primereact/sidebar'
import {
  OperatingRoomDayDTO,
  OperatingRoomEvolutionPointDTO,
  OperatingRoomFilterDTO,
} from '../../../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import { getOperatingRoomDay } from '../../../services/OperatingRoomIndicatorsController'
import useSectionData from '../../UrgencySummary/useSectionData'
import { pointTitleOf } from '../operatingRoomEvolutionModel'
import { filtersKey } from '../operatingRoomFilterModel'
import { formatCount, formatHours, formatPercent } from '../operatingRoomFormat'
import OperatingRoomBars, { OperatingRoomBarRow } from './OperatingRoomBars'

interface OperatingRoomDaySidebarProps {
  /** El punto que se mira (un día o una semana del gráfico); null con el panel cerrado. */
  point: OperatingRoomEvolutionPointDTO | null
  granularity: 'DAY' | 'WEEK'
  /** Los filtros aplicados de la pantalla: el panel pone las fechas del punto. */
  filters: OperatingRoomFilterDTO
  /** «Ver solo este día»: aplica las fechas como período de la pantalla. */
  onViewRange: (from: string, to: string) => void
  /** «Ver cirugías»: las mismas fechas, y la lista. */
  onViewList: (from: string, to: string) => void
  onHide: () => void
}

interface KpiProps {
  label: string
  value: ReactNode
}

function Kpi({ label, value }: KpiProps) {
  return (
    <div className="nv-or-detail__kpi">
      <dt className="nv-or-detail__kpi-label">{label}</dt>
      <dd className="nv-or-detail__kpi-value">{value}</dd>
    </div>
  )
}

interface SectionProps {
  id: string
  title: string
  children: ReactNode
}

function Section({ id, title, children }: SectionProps) {
  return (
    <section className="nv-or-detail__section" aria-labelledby={id}>
      <h3 className="nv-section-title" id={id}>{title}</h3>
      {children}
    </section>
  )
}

const hourLabel = (hour: number): string => `${String(hour).padStart(2, '0')}:00`

/**
 * Detalle de un día (o una semana) del gráfico de evolución, en un panel lateral de Novarium (420 px, a todo el ancho en
 * pantallas angostas). Los números del punto vienen del gráfico (se ven al instante); las horas por sala y las cirugías por
 * hora se piden a `/day` con las fechas del punto y los filtros de la pantalla.
 */
export default function OperatingRoomDaySidebar({
  point, granularity, filters, onViewRange, onViewList, onHide,
}: OperatingRoomDaySidebarProps) {
  const intl = useIntl()
  // Mientras el panel se cierra se sigue mostrando lo último (sin parpadeo de «Cargando»)
  const lastShown = useRef<{ point: OperatingRoomEvolutionPointDTO; filters: OperatingRoomFilterDTO } | null>(null)
  if (point) lastShown.current = { point, filters }
  const shown = point ? { point, filters } : lastShown.current

  const dayFilters: OperatingRoomFilterDTO | null = shown ? { ...shown.filters, from: shown.point.date, to: shown.point.endDate } : null
  const day = useSectionData<OperatingRoomDayDTO>(
    dayFilters === null ? null : `${filtersKey(dayFilters)}|day`,
    (signal) => {
      if (!dayFilters) throw new Error('Panel sin día')
      return getOperatingRoomDay(dayFilters, signal)
    },
  )

  const data = day.data
  const roomRows = useMemo<OperatingRoomBarRow[]>(() => {
    const rooms = (data?.rooms ?? []).filter((room) => room.hoursUsed > 0)
    const max = Math.max(0, ...rooms.map((room) => room.hoursUsed))
    return rooms.map((room) => ({
      key: String(room.roomId ?? room.name),
      label: room.name ?? '',
      fraction: max > 0 ? room.hoursUsed / max : 0,
      valueText: room.occupancyPct === null
        ? formatHours(intl, room.hoursUsed)
        : `${formatHours(intl, room.hoursUsed)} · ${formatPercent(intl, room.occupancyPct)}`,
    }))
  }, [data, intl])

  const hourRows = useMemo<OperatingRoomBarRow[]>(() => {
    const byHour = Array.from({ length: 24 }, (_, hour) => (data?.rooms ?? []).reduce((sum, room) => sum + (room.startedByHour[hour] ?? 0), 0))
    const max = Math.max(0, ...byHour)
    return byHour.flatMap((count, hour) => (count > 0
      ? [{ key: String(hour), label: hourLabel(hour), fraction: max > 0 ? count / max : 0, valueText: formatCount(intl, count) }]
      : []))
  }, [data, intl])

  if (!shown) return null
  const current = shown.point
  const title = pointTitleOf(intl, current, granularity)
  const isDay = granularity === 'DAY'

  return (
    <Sidebar
      visible={point !== null}
      position="right"
      onHide={onHide}
      showCloseIcon={false}
      blockScroll
      className="nv-scope nv-drawer nv-or-detail"
      aria-labelledby="nv-or-detail-title"
    >
      <div className="nv-drawer-layout">
        <header className="nv-drawer-head">
          <h2 className="nv-modal-title nv-or-detail__title" id="nv-or-detail-title">{title}</h2>
          <Button
            type="button"
            text
            className="nv-icon nv-tip"
            icon="pi pi-times"
            aria-label={intl.formatMessage({ id: 'OperatingRoomIndicatorsDetailClose' })}
            data-pr-tooltip={intl.formatMessage({ id: 'OperatingRoomIndicatorsDetailClose' })}
            onClick={onHide}
          />
        </header>

        <div className="nv-drawer-body">
          {/* Los números del punto vienen del gráfico: se ven al instante, sin esperar la consulta del día */}
          <section className="nv-or-detail__section" aria-label={intl.formatMessage({ id: 'OperatingRoomIndicatorsDayKpisLabel' })}>
            <dl className="nv-or-detail__kpis">
              <Kpi label={intl.formatMessage({ id: 'OperatingRoomIndicatorsKpiSurgeries' })} value={formatCount(intl, current.surgeries)} />
              <Kpi label={intl.formatMessage({ id: 'OperatingRoomIndicatorsKpiUrgent' })} value={formatCount(intl, current.urgent)} />
              <Kpi label={intl.formatMessage({ id: 'OperatingRoomIndicatorsKpiScheduled' })} value={formatCount(intl, current.scheduled)} />
              <Kpi label={intl.formatMessage({ id: 'OperatingRoomIndicatorsKpiOutOfAgenda' })} value={formatCount(intl, current.outOfAgenda)} />
              <Kpi label={intl.formatMessage({ id: 'OperatingRoomIndicatorsKpiHours' })} value={formatHours(intl, current.hoursUsed)} />
              <Kpi label={intl.formatMessage({ id: 'OperatingRoomIndicatorsKpiOccupancy' })} value={formatPercent(intl, current.occupancyPct)} />
              <Kpi label={intl.formatMessage({ id: 'OperatingRoomIndicatorsKpiAnnulled' })} value={formatCount(intl, current.annulled)} />
              <Kpi label={intl.formatMessage({ id: 'OperatingRoomIndicatorsKpiRescheduled' })} value={formatCount(intl, current.rescheduled)} />
              <Kpi label={intl.formatMessage({ id: 'OperatingRoomIndicatorsKpiMissed' })} value={formatCount(intl, current.missed)} />
            </dl>
          </section>

          {day.status === 'loading' && (
            <span className="nv-loading nv-or-detail__loading" role="status">
              <ProgressSpinner strokeWidth="4" style={{ width: 16, height: 16 }} />
              <span>{intl.formatMessage({ id: 'OperatingRoomIndicatorsLoading' })}</span>
            </span>
          )}
          {day.status === 'failed' && (
            <div className="nv-or-failed" role="status">
              <p className="nv-note">
                <i className="pi pi-info-circle" aria-hidden="true" />
                {intl.formatMessage({ id: 'OperatingRoomIndicatorsDetailFailed' })}
              </p>
              <Button type="button" link label={intl.formatMessage({ id: 'OperatingRoomIndicatorsRetry' })} onClick={day.retry} />
            </div>
          )}
          {data && (
            <>
              {roomRows.length > 0 && (
                <Section id="nv-or-detail-rooms" title={intl.formatMessage({ id: 'OperatingRoomIndicatorsDetailRooms' })}>
                  <OperatingRoomBars rows={roomRows} ariaLabelledBy="nv-or-detail-rooms" />
                </Section>
              )}
              {hourRows.length > 0 && (
                <Section id="nv-or-detail-hours" title={intl.formatMessage({ id: 'OperatingRoomIndicatorsDetailStartedByHour' })}>
                  <OperatingRoomBars rows={hourRows} ariaLabelledBy="nv-or-detail-hours" />
                </Section>
              )}
            </>
          )}
        </div>

        <footer className="nv-drawer-foot">
          <Button
            type="button"
            link
            label={intl.formatMessage({ id: 'OperatingRoomIndicatorsDetailViewSurgeries' })}
            onClick={() => onViewList(current.date, current.endDate)}
          />
          <Button
            type="button"
            className="buttonMain3"
            label={intl.formatMessage({ id: isDay ? 'OperatingRoomIndicatorsDetailViewOnlyDay' : 'OperatingRoomIndicatorsDetailViewOnlyWeek' })}
            onClick={() => onViewRange(current.date, current.endDate)}
          />
        </footer>
      </div>
    </Sidebar>
  )
}
